/**
 * Die Sitzung: wiederherstellen, anmelden, registrieren, auffrischen, abmelden.
 *
 * Frischt **vorausschauend** auf, statt auf ein `401` zu warten: Ein Timer läuft
 * kurz vor Ablauf des Zugriffstokens. Das erspart der Oberfläche, jede Anfrage
 * wiederholen zu können — und ein `401` behält damit seine eigentliche Bedeutung,
 * nämlich dass die Sitzung wirklich zu Ende ist.
 *
 * Seit ADR 0015 beginnt jeder Seitenaufruf mit `restore()`: einem Auffrischen
 * ohne Rumpf. Gelingt es, lag im `HttpOnly`-Cookie noch eine gültige Sitzung —
 * dann ist man angemeldet, ohne etwas eingegeben zu haben. Das ist der ganze
 * Mechanismus, der ein Neuladen überlebt.
 */
/**
 * Die Typen kommen aus dem **SDK**, nicht aus `@hausmeter/contracts`: Die Web-App
 * kennt die API nur so, wie der erzeugte Client sie beschreibt. Ein direkter Griff
 * in die Verträge würde die Grenze aus `apps/web/CLAUDE.md` überschreiten — und
 * ließe die App gegen etwas übersetzen, das im Client gar nicht steht.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { api, provideAccessToken, whenSessionLost } from "./client.js";
import type { ErrorDetail } from "./registration-fields.js";
import { isRefreshRequest, needsRefresh, REFRESH_LEAD_MS } from "./token-freshness.js";

type CurrentUser = ApiComponents["schemas"]["CurrentUser"];
type AccountDeletionPreview = ApiComponents["schemas"]["AccountDeletionPreview"];
type LoginRequest = ApiComponents["schemas"]["LoginRequest"];
type RegisterRequest = ApiComponents["schemas"]["RegisterRequest"];
type RegistrationStarted = ApiComponents["schemas"]["RegistrationStarted"];
type TokenPair = ApiComponents["schemas"]["TokenPair"];

export type { CurrentUser };
import { accessToken, readSession, writeSession } from "./token-store.js";

/**
 * Der Browser hält den Auffrischungstoken im Cookie; wir fordern ihn nie im
 * Rumpf an. Steht als Konstante, weil dieselbe Angabe an drei Stellen gebraucht
 * wird und eine abweichende davon den Token einmal lesbar zurückgäbe.
 */
const TRANSPORT = "cookie" as const;

/**
 * Was die Oberfläche über die Sitzung weiß.
 *
 * `unknown` ist der Zustand während `restore()`. Ohne ihn zeigte die App beim
 * Neuladen für einen Augenblick die Anmeldeseite und ersetzte sie dann wieder —
 * ein Flackern, das aussieht, als wäre man abgemeldet worden.
 */
export type SessionStatus = "unknown" | "signed-in" | "signed-out";

export interface SessionState {
  readonly status: SessionStatus;
  readonly user: CurrentUser | null;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Der Schnappschuss für `useSyncExternalStore`. Wird nur beim Wechsel ersetzt:
 * React vergleicht ihn mit `Object.is`, und ein bei jedem Aufruf neu gebautes
 * Objekt ergäbe eine Endlosschleife.
 */
let state: SessionState = { status: "unknown", user: null };

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function sessionState(): SessionState {
  return state;
}

/**
 * Versucht, eine im Cookie liegende Sitzung fortzusetzen.
 *
 * Wird einmal beim Start aufgerufen. Scheitert der Versuch — kein Cookie,
 * abgelaufen, widerrufen —, ist das kein Fehler, sondern die Auskunft „nicht
 * angemeldet".
 */
export async function restore(): Promise<void> {
  /**
   * Der laufende Versuch, nicht nur der abgeschlossene. Eine Prüfung auf
   * `state.status` allein reicht nicht: Der Zustand wechselt erst **nach** dem
   * `await`, und React ruft den Effekt im StrictMode zweimal auf — es entstünden
   * zwei Anfragen, von denen die zweite den soeben rotierten Token vorlegt und
   * damit die Diebstahlerkennung auslöst.
   */
  restoring ??= attemptRestore();
  return restoring;
}

let restoring: Promise<void> | undefined;

async function attemptRestore(): Promise<void> {
  if (state.status !== "unknown") return;

  /**
   * **Alles in einem `try`, und der Fehlerfall endet immer in `signed-out`.**
   *
   * Ein `fetch` wirft, wenn gar keine Antwort kommt: API nicht gestartet,
   * falscher Port, von CORS abgewiesen. Ohne dieses `catch` bliebe die Promise
   * abgelehnt, `setState` liefe nie, und die Anwendung stünde dauerhaft im
   * Zustand `unknown` — sichtbar als Startbildschirm, der nicht weitergeht.
   * Das sieht nach einem Absturz aus, obwohl nur die API fehlt.
   *
   * „Nicht wiederherstellbar" heißt in jedem dieser Fälle dasselbe wie ein
   * abgelaufener Token: nicht angemeldet. Die Anmeldeseite sagt anschließend
   * selbst, wenn die API nicht erreichbar ist.
   */
  try {
    /**
     * Leerer Rumpf — der Server nimmt den Token aus dem Cookie und antwortet
     * genauso: mit einem rotierten Cookie und ohne `refreshToken` im Rumpf. Es
     * braucht hier kein `sessionTransport`, weil der Weg hinein den Weg hinaus
     * bereits festlegt.
     */
    const { data, error } = await api.POST("/auth/refresh", { body: {} });

    if (error || !data) {
      setState({ status: "signed-out", user: null });
      return;
    }

    await adopt(data);
  } catch {
    setState({ status: "signed-out", user: null });
  }
}

export async function login(credentials: {
  email: string;
  password: string;
  rememberMe: boolean;
}): Promise<void> {
  const body: LoginRequest = { ...credentials, sessionTransport: TRANSPORT };
  const { data, error } = await api.POST("/auth/login", { body });
  if (error || !data) throw toError(error, "Anmeldung fehlgeschlagen.");
  await adopt(data);
}

export async function startRegistration(input: RegisterRequest): Promise<RegistrationStarted> {
  const { data, error } = await api.POST("/auth/register", { body: input });
  if (error || !data) throw toError(error, "Die Registrierung konnte nicht begonnen werden.");
  return data;
}

/**
 * Eine Einladung mit dem angemeldeten Konto annehmen (HM-081). Die Adresse des
 * Kontos muss der eingeladenen entsprechen; sonst sagt es der Server.
 */
export async function acceptInvitation(token: string): Promise<void> {
  const { error } = await api.POST("/invitations/accept", { body: { token } });
  if (error) throw toError(error, "Die Einladung konnte nicht angenommen werden.");
}

/**
 * Den Sicherheitscode noch einmal anfordern.
 *
 * **Der bisherige verfällt damit.** Wer die alte Nachricht noch offen hat, muss
 * die neue nehmen — deshalb sagt die Oberfläche es dazu.
 */
export async function resendRegistrationCode(email: string): Promise<RegistrationStarted> {
  const { data, error } = await api.POST("/auth/register/resend", { body: { email } });
  if (error || !data) throw toError(error, "Der Code konnte nicht erneut gesendet werden.");
  return data;
}

/**
 * Schritt zwei: der Code — **hier** entsteht das Konto, und die Sitzung
 * beginnt.
 *
 * Deshalb steht `rememberMe` hier und nicht im ersten Schritt: Es wählt die
 * Laufzeit einer Sitzung, die es vorher nicht gibt.
 */
export async function confirmRegistration(input: {
  email: string;
  code: string;
  rememberMe: boolean;
}): Promise<void> {
  const { data, error } = await api.POST("/auth/register/confirm", {
    body: { ...input, sessionTransport: TRANSPORT },
  });

  if (error || !data) throw toError(error, "Der Sicherheitscode konnte nicht geprüft werden.");
  await adopt(data);
}

/**
 * Passwort ändern.
 *
 * **Die Antwort trägt ein neues Tokenpaar, und es muss übernommen werden.** Die
 * Änderung entwertet serverseitig jede Sitzung dieses Nutzers — auch die
 * aufrufende. Wer das zurückkommende Paar wegwirft, arbeitet noch bis zum
 * Ablauf des Zugriffstokens weiter und steht dann ohne Vorwarnung auf der
 * Anmeldeseite.
 */
export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const { data, error } = await api.POST("/auth/password", {
    body: { ...input, sessionTransport: TRANSPORT },
  });

  if (error || !data) throw toError(error, "Das Passwort konnte nicht geändert werden.");
  await adopt(data);
}

/**
 * Die erste eigene Anlage — aus dem Bewohnerkonto wird ein Standardkonto
 * (HM-116). Gibt die Kennung der neuen Anlage zurück.
 *
 * **Danach wird sofort aufgefrischt, und zwar mit dem Nutzer.** Der
 * Zugriffstoken nennt noch den Account des Vermieters; erst die Rotation wählt
 * den eigenen. Das gewöhnliche Auffrischen lädt den Nutzer nicht neu — die
 * Seitenleiste zeigte dann weiter die Hülle eines Bewohners, und `GET /sites`
 * ginge mit der Rolle `resident` hinaus.
 */
export async function createOwnSite(siteName: string): Promise<string> {
  const { data, error } = await api.POST("/auth/me/own-site", { body: { siteName } });
  if (error || !data) throw toError(error, "Die Anlage konnte nicht angelegt werden.");
  await renewWithUser();
  return data.siteId;
}

/**
 * Auffrischen **und** den Nutzer neu laden. Über denselben Platz wie `refresh`,
 * damit nie zwei Anfragen denselben Auffrischungstoken vorlegen.
 */
async function renewWithUser(): Promise<void> {
  await refreshing?.catch(() => undefined);
  refreshing = (async () => {
    const { data, error } = await api.POST("/auth/refresh", { body: {} });
    if (error || !data) {
      endSession();
      return;
    }
    await adopt(data);
  })().finally(() => {
    refreshing = undefined;
  });
  return refreshing;
}

/** Was das Löschen dieses Kontos mitnimmt (HM-163). */
export async function accountDeletionPreview(): Promise<AccountDeletionPreview> {
  const { data, error } = await api.GET("/auth/me/deletion");
  if (error || !data) throw toError(error, "Die Auswirkungen konnten nicht geladen werden.");
  return data;
}

/**
 * Das Konto löschen (HM-163, ADR 0044). Danach gibt es keine Sitzung mehr —
 * der Server hat die Kette gelöscht und das Cookie mit; hier endet der
 * Zustand im Speicher, und die Anwendung zeigt die Anmeldung.
 */
export async function deleteAccount(password: string): Promise<void> {
  const { error } = await api.POST("/auth/me/deletion", { body: { password } });
  if (error) throw toError(error, "Das Konto konnte nicht gelöscht werden.");
  endSession();
}

/**
 * „Passwort vergessen" anstoßen (HM-026).
 *
 * **Wirft nur, wenn der Server nicht erreichbar war oder das Kontingent
 * erschöpft ist.** Ob es die Adresse gibt, sagt er nicht — und die Oberfläche
 * darf es deshalb auch nicht andeuten.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await api.POST("/auth/password/forgot", { body: { email } });
  if (error) throw toError(error, "Die Anfrage konnte nicht gestellt werden.");
}

/**
 * Neues Passwort mit dem Token aus der E-Mail setzen.
 *
 * Die Antwort trägt ein Tokenpaar, und es wird übernommen: Wer sein Passwort
 * gerade gesetzt hat, ist angemeldet. Ihn danach auf die Anmeldeseite zu
 * schicken, damit er dasselbe Passwort noch einmal eintippt, wäre ein Schritt
 * ohne Zweck.
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const { data, error } = await api.POST("/auth/password/reset", {
    body: { token, newPassword, sessionTransport: TRANSPORT },
  });
  if (error || !data) throw toError(error, "Das Passwort konnte nicht gesetzt werden.");
  await adopt(data);
}

/**
 * Adresse bestätigen.
 *
 * Gibt den bestätigten Nutzer zurück, **ohne** eine Sitzung zu beginnen: Der
 * Link wird oft in einem Browser geöffnet, in dem niemand angemeldet ist, und
 * eine Bestätigung ist kein Nachweis, dass hier der Kontoinhaber sitzt — nur
 * dass jemand Zugriff auf das Postfach hat.
 */
export async function verifyEmail(token: string): Promise<CurrentUser> {
  const { data, error } = await api.POST("/auth/email/verify", { body: { token } });
  if (error || !data) throw toError(error, "Die Adresse konnte nicht bestätigt werden.");

  // Ist gerade jemand angemeldet, bekommt er den neuen Stand sofort zu sehen —
  // sonst stünde der Hinweis „nicht bestätigt" noch bis zum nächsten Laden da.
  if (state.status === "signed-in" && state.user?.id === data.id) replaceUser(data);
  return data;
}

/** Die Bestätigungsnachricht noch einmal anfordern. */
export async function resendVerification(): Promise<void> {
  const { error } = await api.POST("/auth/email/verify/resend", {});
  if (error) throw toError(error, "Die Nachricht konnte nicht angefordert werden.");
}

/**
 * Ersetzt den bekannten Nutzer im Sitzungszustand.
 *
 * Für die Endpunkte, die einen geänderten `CurrentUser` zurückgeben — das
 * Profilbild etwa. Ohne das müsste die Oberfläche `restore()` aufrufen, also
 * eine Tokenrotation auslösen, nur weil sich ein Zeitstempel geändert hat.
 */
export function replaceUser(user: CurrentUser): void {
  if (state.status !== "signed-in") return;
  setState({ status: "signed-in", user });
}

export async function logout(): Promise<void> {
  /**
   * Kein Token im Rumpf: Der Server nimmt ihn aus dem Cookie und löscht es.
   * Schlägt der Aufruf fehl, ist die lokale Sitzung trotzdem zu beenden — sonst
   * bliebe man scheinbar angemeldet.
   */
  await api.POST("/auth/logout", { body: {} }).catch(() => undefined);
  endSession();
}

/** Übernimmt ein frisches Tokenpaar und lädt den angemeldeten Nutzer. */
async function adopt(tokens: TokenPair): Promise<void> {
  const me = await loadUser(tokens);

  writeSession({
    accessToken: tokens.accessToken,
    expiresAt: Date.now() + tokens.expiresIn * 1000,
    accountId: me.accounts[0]?.id ?? "",
  });

  scheduleRefresh(tokens.expiresIn);
  setState({ status: "signed-in", user: me });
}

/**
 * Der Nutzer wird geladen, **bevor** die Sitzung gilt: Erst danach steht fest,
 * zu welchem Account sie gehört. Deshalb reicht der Token hier von Hand herein
 * statt über den Speicher.
 */
async function loadUser(tokens: TokenPair): Promise<CurrentUser> {
  const { data, error } = await api.GET("/auth/me", {
    headers: { Authorization: `Bearer ${tokens.accessToken}` },
  });
  if (error || !data) throw toError(error, "Das Konto konnte nicht geladen werden.");
  return data;
}

function scheduleRefresh(expiresInSeconds: number): void {
  clearTimeout(refreshTimer);
  // Nie negativ und nie sofort: Bei sehr kurzer Gültigkeit lieber knapp vor Ablauf
  // als in einer Schleife.
  const delay = Math.max(5_000, expiresInSeconds * 1000 - REFRESH_LEAD_MS);
  refreshTimer = setTimeout(() => void refresh().catch(() => undefined), delay);
}

/**
 * Der Zugriffstoken für eine Anfrage — **aufgefrischt, bevor sie hinausgeht**
 * (HM-098).
 *
 * Der Timer allein trägt die Sitzung nicht: Ein Browser auf dem Telefon hält
 * Timer an, sobald der Tab in den Hintergrund geht. Wer Stunden später
 * zurückkehrt, hat einen abgelaufenen Token im Speicher und ein gültiges
 * Cookie — und React Query lädt beim Sichtbarwerden sofort nach. Ging die
 * Anfrage mit dem alten Token hinaus, kam `401`, und die Anwendung schloss auf
 * das Ende der Sitzung: Anmeldeseite, obwohl ein Neuladen sofort wieder
 * hineinführte. Deshalb fragt der Client hier nach, und ein Token, der nicht
 * mehr trägt, wird erst erneuert. Die Auffrischung selbst wartet nicht auf
 * sich; sie geht ohne Token hinaus, der Browser hängt das Cookie an.
 *
 * Scheitert die Auffrischung am Netz, scheitert die Anfrage mit demselben
 * Fehler — und **nicht** mit einem `401`, das die Sitzung beendete.
 */
export async function ensureFreshToken(request: Request): Promise<string | null> {
  if (isRefreshRequest(request.url)) return null;
  const current = readSession();
  if (!current) return null;
  if (needsRefresh(current.expiresAt, Date.now())) await refresh();
  return accessToken();
}

/**
 * **Einmal zur Zeit.** Der Timer, eine wartende Anfrage und das Aufwachen des
 * Tabs können im selben Augenblick auffrischen wollen; zwei Anfragen legten
 * nacheinander denselben Auffrischungstoken vor, und die zweite löste die
 * Diebstahlerkennung aus — die Sitzung wäre zu Ende, gerade weil sie
 * fortgesetzt werden sollte. Alle warten deshalb auf dieselbe Promise.
 */
let refreshing: Promise<void> | undefined;

function refresh(): Promise<void> {
  refreshing ??= attemptRefresh().finally(() => {
    refreshing = undefined;
  });
  return refreshing;
}

async function attemptRefresh(): Promise<void> {
  const previous = readSession();
  if (!previous) return;

  const call = () => api.POST("/auth/refresh", { body: {} });
  let result: Awaited<ReturnType<typeof call>>;
  try {
    result = await call();
  } catch (cause) {
    /**
     * Keine Antwort — Netz weg, Funkloch, API gerade nicht da. Das ist kein
     * Ende der Sitzung: Das Cookie gilt weiter, und der nächste Versuch kann
     * gelingen. Der Timer versucht es bald noch einmal; wer gerade wartet,
     * bekommt den Fehler und nicht ein `401`.
     */
    scheduleRetry();
    throw cause;
  }
  const { data, error } = result;

  if (error || !data) {
    // Auffrischen ist fehlgeschlagen — abgelaufen, widerrufen oder als gestohlen
    // erkannt. In jedem Fall ist die Sitzung vorbei.
    endSession();
    return;
  }

  writeSession({
    accessToken: data.accessToken,
    expiresAt: Date.now() + data.expiresIn * 1000,
    accountId: previous.accountId,
  });

  scheduleRefresh(data.expiresIn);
  notify();
}

/** Nach einem Netzfehler: bald noch einmal, nicht in einer Schleife. */
function scheduleRetry(): void {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => void refresh().catch(() => undefined), 5_000);
}

/**
 * Beim Aufwachen des Tabs vorausschauend auffrischen, statt auf die erste
 * Anfrage zu warten. `visibilitychange` deckt den Wechsel aus dem Hintergrund
 * ab, `pageshow` die Rückkehr aus dem Vor-Zurück-Speicher des Browsers, in
 * dem die Seite samt Zustand eingefroren lag. Ein gültiger Token bleibt
 * unangetastet — sonst rotierte das Cookie bei jedem Blick auf ein anderes
 * Fenster.
 */
function refreshOnWake(): void {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const current = readSession();
  if (!current || !needsRefresh(current.expiresAt, Date.now())) return;
  void refresh().catch(() => undefined);
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", refreshOnWake);
  window.addEventListener("pageshow", refreshOnWake);
}

function endSession(): void {
  clearTimeout(refreshTimer);
  refreshTimer = undefined;
  writeSession(null);
  setState({ status: "signed-out", user: null });
}

function setState(next: SessionState): void {
  state = next;
  notify();
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Ein Fehler des Servers samt seiner Einzelheiten.
 *
 * **Die Einzelheiten gingen bis dahin verloren**: Übrig blieb „Die Anfrage
 * entspricht nicht dem Vertrag.", und welches Feld gemeint war, stand nur noch in
 * der Antwort, die niemand las. Wer ein Formular hat, holt sie hier ab
 * (`lib/registration-fields.ts`).
 */
export class RequestError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly details: readonly ErrorDetail[] | undefined,
  ) {
    super(message);
  }
}

function toError(error: unknown, fallback: string): Error {
  const body = (error as { error?: { message?: string; code?: string; details?: ErrorDetail[] } })
    ?.error;
  return new RequestError(body?.message ?? fallback, body?.code, body?.details);
}

// Ein `401` an anderer Stelle beendet die Sitzung ebenfalls — etwa wenn der Token
// serverseitig widerrufen wurde, während die Seite offen stand.
whenSessionLost(endSession);
// Und jede Anfrage holt ihren Token hier — aufgefrischt, wenn er nicht mehr trägt.
provideAccessToken(ensureFreshToken);
